import { useState } from "react";
import { Eye, EyeOff, ArrowRight, Loader2, User, Lock } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import logo from "../../images/logo/Aluex-Logo.png";
import { LoginAPI } from "../../utlis/apiService";
import { useNavigate } from "react-router-dom";

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const response = await LoginAPI.loginUser(username, password);
      if (response.token) {
        sessionStorage.setItem("user", JSON.stringify(response));
        navigate("/dashboard");
      } else {
        setError("Invalid credentials. Please try again.");
      }
    } catch (err) {
      setError("Login failed. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 font-plus px-4">
      <div className="w-full max-w-md">

        {/* Card */}
        <div className="bg-white border border-gray-200 shadow-sm p-10">

          {/* Logo + Header */}
          <div className="mb-8 text-center">
            <img src={logo} alt="Aluex Logo" className="h-10 object-contain mx-auto mb-6" />
            <p className="text-xs font-black text-primary uppercase tracking-[3px] mb-1">
              Welcome Back
            </p>
            <h1 className="text-2xl font-black text-gray-900">Sign in to your account</h1>
          </div>

          {/* Form */}
          <form onSubmit={handleLogin} className="space-y-5">

            {/* Username */}
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
                Username / Email
              </label>
              <div className="flex items-center gap-3 border border-gray-200 px-4 py-3 focus-within:border-primary transition-colors duration-200">
                <User size={15} className="text-gray-400 flex-shrink-0" strokeWidth={2.5} />
                <input
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  disabled={loading}
                  className="flex-1 text-sm font-medium text-gray-800 placeholder-gray-300 focus:outline-none bg-transparent disabled:opacity-50"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="text-xs font-bold text-gray-500 uppercase tracking-wider block mb-1.5">
                Password
              </label>
              <div className="flex items-center gap-3 border border-gray-200 px-4 py-3 focus-within:border-primary transition-colors duration-200">
                <Lock size={15} className="text-gray-400 flex-shrink-0" strokeWidth={2.5} />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  className="flex-1 text-sm font-medium text-gray-800 placeholder-gray-300 focus:outline-none bg-transparent disabled:opacity-50"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-gray-400 hover:text-primary transition-colors flex-shrink-0"
                  tabIndex={-1}
                >
                  {showPassword
                    ? <EyeOff size={15} strokeWidth={2.5} />
                    : <Eye size={15} strokeWidth={2.5} />
                  }
                </button>
              </div>
            </div>

            {/* Error */}
            <AnimatePresence>
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="flex items-center gap-2 bg-red-50 border border-red-200 px-4 py-3"
                >
                  <div className="w-1.5 h-1.5 bg-red-500 rounded-full flex-shrink-0" />
                  <p className="text-xs font-semibold text-red-600">{error}</p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading || !username || !password}
              className="w-full flex items-center justify-center gap-3 py-3.5 font-black text-sm uppercase tracking-widest text-white transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ backgroundColor: loading ? "#0F3D75" : "#3F80F7" }}
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin" strokeWidth={3} />
                  Signing In...
                </>
              ) : (
                <>
                  Log In Now
                  <ArrowRight size={16} strokeWidth={3} />
                </>
              )}
            </button>

          </form>
        </div>

      </div>
    </div>
  );
};

export default Login;
